import { Injectable } from '@angular/core';
import { TdDialogService } from '@covalent/core/dialogs';
import { TdLoadingService } from '@covalent/core/loading';
import { _, TranslateService } from '@ngx-translate/core';
import { AwsS3MultipartOptions } from '@uppy/aws-s3-multipart';
import { UppyFile } from '@uppy/core';
import _pick from 'lodash-es/pick';
import { finalize } from 'rxjs/operators';

import { ProjectClinicalCaseControllerApi } from '../sdk-api/api/projectClinicalCaseController.service';
import { ProjectClinicalCaseStorageControllerApi } from '../sdk-api/api/projectClinicalCaseStorageController.service';
import { ClinicalCase } from '../sdk-api/model/clinicalCase';
import { ProjectWithRelations } from '../sdk-api/model/projectWithRelations';
import { ClinicalCaseCommonService } from './clinical-case-common.service';
import { ConfigurationService } from './configuration.service';

@Injectable({
  providedIn: 'root',
})
export class ClinicalCaseService extends ClinicalCaseCommonService {
  constructor(
    configurationService: ConfigurationService,
    private clinicalCaseApi: ProjectClinicalCaseControllerApi,
    dialogService: TdDialogService,
    private loaderService: TdLoadingService,
    translateService: TranslateService,
    private projectClinicalCaseStorageApi: ProjectClinicalCaseStorageControllerApi,
  ) {
    super(configurationService, dialogService, translateService);
  }

  getMultipartConfiguration(
    project: ProjectWithRelations,
  ): AwsS3MultipartOptions {
    return {
      createMultipartUpload: (file: UppyFile) => {
        const { meta } = file;
        const metaFields = _pick(meta, ['type', 'doppler']);
        return this.projectClinicalCaseStorageApi
          .projectClinicalCaseStorageControllerUpload(
            project.id,
            this.currentClinicalCase.id,
            JSON.stringify(metaFields) as never,
          )
          .toPromise();
      },
      listParts: (_file, { uploadId, key }) =>
        this.projectClinicalCaseStorageApi
          .projectClinicalCaseStorageControllerUploadedParts(
            project.id,
            this.currentClinicalCase.id,
            uploadId,
            key,
          )
          .toPromise(),
      prepareUploadParts: (_file, partData) =>
        this.projectClinicalCaseStorageApi
          .projectClinicalCaseStorageControllerSignPartsUpload(
            project.id,
            this.currentClinicalCase.id,
            partData.uploadId,
            partData,
          )
          .toPromise(),
      abortMultipartUpload: (_file, { uploadId, key }) =>
        this.projectClinicalCaseStorageApi
          .projectClinicalCaseStorageControllerAbortMultipartUpload(
            project.id,
            this.currentClinicalCase.id,
            uploadId,
            key,
          )
          .toPromise(),
      completeMultipartUpload: (_file, { uploadId, key, parts }) =>
        this.projectClinicalCaseStorageApi
          .projectClinicalCaseStorageControllerComplete(
            project.id,
            this.currentClinicalCase.id,
            uploadId,
            parts,
            key,
          )
          .toPromise(),
    };
  }

  openConfirmDeleteClinicalCase(
    project: ProjectWithRelations,
    clinicalCase: ClinicalCase,
  ): void {
    const dialog = this.dialogService.openConfirm({
      title: this.translateService.instant(_('CLINICAL-CASE.DELETE.TITLE')),
      message: this.translateService.instant(
        _('CLINICAL-CASE.DELETE.MESSAGE'),
        {
          clinicalCase,
        },
      ),
      acceptButton: this.translateService.instant(_('LABEL.CONFIRM')),
      cancelButton: this.translateService.instant(_('LABEL.CLOSE')),
      disableClose: true,
    });

    dialog.afterClosed().subscribe({
      next: (response) => {
        if (response) {
          this.loaderService.register();
          this.clinicalCaseApi
            .projectClinicalCaseControllerDeleteById(
              project.id,
              clinicalCase.id,
            )
            .pipe(finalize(() => this.loaderService.resolve()))
            .subscribe({
              next: async () =>
                await this.openConfirmClinicalCaseDeleted(clinicalCase),
            });
        }
      },
    });
  }
}
